/**
 * THE SOUND OF THE PLACE.
 *
 * An arcade light-gun game is half audio (ROUNDTABLE.md, A-16). A silent
 * version of this is not a quieter version, it is a worse and different game:
 * the shotgun report is most of what makes pulling the trigger feel like
 * anything at all.
 *
 * Every sound here is SYNTHESISED — oscillators, noise buffers, filters and
 * envelopes. There is not one audio file in this project. Two reasons, and
 * both were worth the extra work:
 *
 *   1. The CSP stays at `media-src 'none'` with no CDN and no fetches. The
 *      whole app is one origin's worth of JS and nothing else.
 *   2. The entire kit costs a few kilobytes of code instead of a megabyte of
 *      samples, on a game whose players are on phone data.
 *
 * ── Autoplay ────────────────────────────────────────────────────────────────
 * Browsers start an AudioContext suspended until a user gesture. `unlock()`
 * must therefore be called from inside a real tap handler — it is wired to the
 * same tap that takes the gun off the wall, which every player makes.
 */

export type Sfx =
  | 'shot'
  | 'dryfire'
  | 'rack'
  | 'reloaded'
  | 'hit'
  | 'trophy'
  | 'doe'
  | 'bugle'
  | 'critter'
  | 'skunk'
  | 'coin'
  | 'tick'
  | 'go'
  | 'cheer'
  | 'waveend'
  | 'legend'
  | 'ui'
  | 'escape';

/** What the woods sound like when nobody is shooting. */
export type Ambience = 'none' | 'dawn' | 'day' | 'dusk' | 'wind' | 'night';

export class AudioKit {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private ambGain: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;

  /** Live ambience nodes, so the bed can be torn down and rebuilt per site. */
  private ambNodes: AudioScheduledSourceNode[] = [];
  private ambTimer: number | null = null;
  private ambKind: Ambience = 'none';

  private _muted = false;
  private _musicOn = true;
  private _volume = 0.85;

  get muted() {
    return this._muted;
  }

  get volume() {
    return this._volume;
  }

  get ready() {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  /** Call from a user gesture. Idempotent. */
  async unlock(): Promise<void> {
    if (!this.ctx) {
      const Ctor: typeof AudioContext | undefined =
        window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.targetGain();
      this.master.connect(this.ctx.destination);
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.0;
      this.musicGain.connect(this.master);
      this.ambGain = this.ctx.createGain();
      this.ambGain.gain.value = 0.0;
      this.ambGain.connect(this.master);
      this.noise = makeNoise(this.ctx, 2);
    }
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        /* the next gesture will try again */
      }
    }
  }

  private targetGain(): number {
    return this._muted ? 0 : this._volume;
  }

  private applyGain() {
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(this.targetGain(), this.ctx.currentTime, 0.02);
    }
  }

  setMuted(muted: boolean) {
    this._muted = muted;
    this.applyGain();
    if (muted) this.stopAmbience();
  }

  /** 0 … 1. Separate from mute, so turning it down does not lose the setting. */
  setVolume(v: number) {
    this._volume = Math.max(0, Math.min(1, v));
    this.applyGain();
  }

  setMusic(on: boolean) {
    this._musicOn = on;
    if (!on) this.stopMusic();
  }

  // ── one-shots ────────────────────────────────────────────────────────────

  play(sfx: Sfx, detune = 0) {
    const ctx = this.ctx;
    if (!ctx || !this.master || this._muted) return;
    const t = ctx.currentTime;
    switch (sfx) {
      case 'shot':
        this.shotgun(t);
        break;
      case 'dryfire':
        this.click(t, 900, 0.05, 0.12);
        this.click(t + 0.04, 420, 0.06, 0.08);
        break;
      case 'rack':
        this.click(t, 1800, 0.04, 0.14);
        this.click(t + 0.11, 1200, 0.05, 0.16);
        this.click(t + 0.2, 700, 0.06, 0.12);
        break;
      case 'reloaded':
        // The breech closing. Heavier and lower than the rack that opened it,
        // so "the gun is ready" is audible without looking at the shell row.
        this.click(t, 520, 0.07, 0.2);
        this.thump(t, 150, 0.09, 0.22);
        break;
      case 'hit':
        this.thump(t, 90, 0.16, 0.5);
        this.burst(t, 0.09, 2200, 0.18);
        break;
      case 'trophy':
        this.arpeggio(t, [523.25, 659.25, 783.99, 1046.5], 0.085, 0.24);
        this.thump(t, 70, 0.4, 0.5);
        break;
      case 'doe':
        // A wrong, sour interval — a minor second, low and buzzy. It should
        // feel like a mistake before anyone reads the words on screen.
        this.tone(t, 180, 0.5, 'sawtooth', 0.22);
        this.tone(t + 0.005, 170, 0.5, 'sawtooth', 0.22);
        break;
      case 'bugle':
        this.bugle(t, detune);
        break;
      case 'critter':
        this.tone(t, 880 + detune * 40, 0.09, 'square', 0.12);
        this.tone(t + 0.06, 1320 + detune * 40, 0.08, 'square', 0.1);
        break;
      case 'skunk':
        this.burst(t, 0.35, 500, 0.2);
        this.tone(t, 110, 0.35, 'sawtooth', 0.14);
        break;
      case 'coin':
        this.tone(t, 1046.5, 0.06, 'square', 0.14);
        this.tone(t + 0.07, 1567.98, 0.16, 'square', 0.14);
        break;
      case 'tick':
        // Not a click — a woodblock. A countdown you can hear over a noisy bar.
        this.tone(t, 660, 0.09, 'square', 0.1);
        this.click(t, 1400, 0.03, 0.08);
        break;
      case 'go':
        this.tone(t, 880, 0.14, 'square', 0.16);
        this.tone(t + 0.02, 1318.5, 0.2, 'triangle', 0.12);
        break;
      case 'escape':
        this.tone(t, 330, 0.18, 'triangle', 0.08);
        this.tone(t + 0.1, 247, 0.22, 'triangle', 0.08);
        break;
      case 'waveend':
        // A falling third, dry. The wave is over; it is not a celebration.
        this.tone(t, 392, 0.22, 'triangle', 0.13);
        this.tone(t + 0.14, 311.1, 0.34, 'triangle', 0.13);
        break;
      case 'legend':
        // The one sound in the game that is meant to make a player sit up. A
        // low horn under a slow noise swell, three seconds long.
        this.horn(t);
        break;
      case 'ui':
        this.click(t, 2200, 0.025, 0.07);
        break;
      case 'cheer':
        this.crowd(t);
        break;
    }
  }

  /** The legend's horn. Two octaves of detuned saw, opening slowly. */
  private horn(t: number) {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.2, t + 0.5);
    g.gain.setValueAtTime(0.2, t + 1.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 3);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(300, t);
    lp.frequency.linearRampToValueAtTime(1400, t + 1.2);
    lp.Q.value = 2;
    lp.connect(g).connect(this.master!);
    for (const [f, d] of [
      [55, -6],
      [55, 8],
      [110, 0],
      [82.4, 4],
    ] as const) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      o.detune.value = d;
      o.connect(lp);
      o.start(t);
      o.stop(t + 3.1);
    }
  }

  /**
   * The shotgun. Five layers, because one is a pop:
   *   · the hammer falling, 8 ms ahead of everything else;
   *   · a hard noise transient through a fast-closing low-pass — the crack;
   *   · a sine sweeping 170 → 38 Hz — the body you feel more than hear;
   *   · a long, quiet noise tail — the report coming back off the treeline;
   *   · a small brass tick as the hull clears, a quarter-second later.
   *
   * The hammer and the hull are the two that were added after playing it: on a
   * phone speaker the low body is most of what a small driver refuses to
   * reproduce, so the mechanical detail either side of the bang is doing more
   * work than the bang. Both are cheap and neither is loud.
   */
  private shotgun(at: number) {
    const ctx = this.ctx!;

    // The hammer goes at the caller's instant and the bang 8 ms later, rather
    // than the bang at `at` and the hammer 8 ms earlier — you cannot schedule
    // into the past, and 8 ms of extra latency is a third of a frame.
    this.click(at, 3000, 0.012, 0.09);
    const t = at + 0.008;

    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(6000, t);
    lp.frequency.exponentialRampToValueAtTime(300, t + 0.18);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.75, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
    src.connect(lp).connect(g).connect(this.master!);
    src.start(t);
    src.stop(t + 0.32);

    const body = ctx.createOscillator();
    body.type = 'sine';
    body.frequency.setValueAtTime(170, t);
    body.frequency.exponentialRampToValueAtTime(38, t + 0.16);
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0.0001, t);
    bg.gain.exponentialRampToValueAtTime(0.6, t + 0.006);
    bg.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
    body.connect(bg).connect(this.master!);
    body.start(t);
    body.stop(t + 0.25);

    // The tail. Quiet, band-limited, and long enough to suggest a valley.
    const tail = ctx.createBufferSource();
    tail.buffer = this.noise;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 900;
    bp.Q.value = 0.7;
    const tg = ctx.createGain();
    tg.gain.setValueAtTime(0.0001, t + 0.03);
    tg.gain.exponentialRampToValueAtTime(0.075, t + 0.09);
    tg.gain.exponentialRampToValueAtTime(0.0001, t + 0.85);
    tail.connect(bp).connect(tg).connect(this.master!);
    tail.start(t + 0.03);
    tail.stop(t + 0.9);

    // The hull clearing the breech.
    this.click(t + 0.24, 2600, 0.03, 0.06);
  }

  // ── ambience ─────────────────────────────────────────────────────────────

  /**
   * The bed under a wave.
   *
   * The first playable had nothing here, and the woods between shots were
   * *silent* — which is not atmospheric, it is the sound of a page that has
   * stopped working. Every site now gets a wind layer (looping noise through a
   * band-pass with a slow LFO on the gain, so it breathes) plus a scattered
   * voice appropriate to the hour: birds at dawn, crickets at dusk and night,
   * nothing at all in the frozen one but wind.
   *
   * Total cost is one looping buffer source, two filters, an oscillator and a
   * `setInterval` that fires every few seconds. It idles at effectively zero.
   */
  startAmbience(kind: Ambience) {
    if (!this.ctx || !this.ambGain || this._muted) return;
    if (kind === 'none') {
      this.stopAmbience();
      return;
    }
    if (this.ambKind === kind && this.ambNodes.length) return;
    this.stopAmbience();
    this.ambKind = kind;

    const ctx = this.ctx;
    const t = ctx.currentTime;

    const wind = ctx.createBufferSource();
    wind.buffer = this.noise;
    wind.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = kind === 'night' ? 340 : kind === 'wind' ? 520 : 420;
    bp.Q.value = 0.6;
    const swell = ctx.createGain();
    swell.gain.value = kind === 'wind' ? 0.5 : 0.34;

    // A slow LFO on the wind's gain. Constant-amplitude noise reads as tape
    // hiss; noise that rises and falls over eight seconds reads as weather.
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.12;
    const lfoAmt = ctx.createGain();
    lfoAmt.gain.value = 0.22;
    lfo.connect(lfoAmt).connect(swell.gain);

    wind.connect(bp).connect(swell).connect(this.ambGain);
    wind.start(t);
    lfo.start(t);
    this.ambNodes.push(wind, lfo);

    this.ambGain.gain.cancelScheduledValues(t);
    this.ambGain.gain.setValueAtTime(0.0001, t);
    this.ambGain.gain.exponentialRampToValueAtTime(kind === 'wind' ? 0.09 : 0.07, t + 1.6);

    // The scattered voice. Deliberately sparse and quiet — an ambience you
    // notice is an ambience that will be turned off by the third wave.
    if (kind !== 'wind') {
      this.ambTimer = window.setInterval(() => this.ambientVoice(kind), kind === 'dawn' ? 2600 : 3400);
    }
  }

  stopAmbience() {
    if (this.ambTimer !== null) {
      clearInterval(this.ambTimer);
      this.ambTimer = null;
    }
    if (this.ambGain && this.ctx) {
      this.ambGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.25);
    }
    for (const n of this.ambNodes) {
      try {
        n.stop(this.ctx ? this.ctx.currentTime + 0.6 : 0);
      } catch {
        /* already stopped */
      }
    }
    this.ambNodes = [];
    this.ambKind = 'none';
  }

  private ambientVoice(kind: Ambience) {
    const ctx = this.ctx;
    if (!ctx || !this.ambGain || this._muted) return;
    // Most intervals do nothing. Regularly spaced birdsong is a metronome.
    if (Math.random() > 0.55) return;
    const t = ctx.currentTime + Math.random() * 0.9;

    if (kind === 'dawn' || kind === 'day') {
      // Two or three quick rising whistles.
      const base = 1800 + Math.random() * 1400;
      const n = 2 + Math.floor(Math.random() * 2);
      for (let i = 0; i < n; i++) {
        const o = ctx.createOscillator();
        o.type = 'sine';
        const s = t + i * 0.085;
        o.frequency.setValueAtTime(base * (0.9 + Math.random() * 0.2), s);
        o.frequency.exponentialRampToValueAtTime(base * 1.35, s + 0.05);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, s);
        g.gain.exponentialRampToValueAtTime(0.05, s + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, s + 0.07);
        o.connect(g).connect(this.ambGain);
        o.start(s);
        o.stop(s + 0.09);
      }
    } else {
      // Crickets: a short buzz of clipped square pulses.
      const f = 2400 + Math.random() * 900;
      for (let i = 0; i < 5; i++) {
        const s = t + i * 0.035;
        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.value = f;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, s);
        g.gain.exponentialRampToValueAtTime(0.02, s + 0.004);
        g.gain.exponentialRampToValueAtTime(0.0001, s + 0.02);
        o.connect(g).connect(this.ambGain);
        o.start(s);
        o.stop(s + 0.03);
      }
    }
  }

  /** A buck's grunt-bugle. Two detuned saws under a formant-ish band-pass. */
  private bugle(t: number, detune: number) {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.14, t + 0.05);
    g.gain.setValueAtTime(0.14, t + 0.3);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.setValueAtTime(520 + detune * 30, t);
    bp.frequency.linearRampToValueAtTime(360 + detune * 30, t + 0.6);
    bp.Q.value = 3.5;
    g.connect(this.master!);
    bp.connect(g);
    for (const off of [0, 7]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = 196 + detune * 12;
      o.detune.value = off;
      o.connect(bp);
      o.start(t);
      o.stop(t + 0.72);
    }
  }

  /** Bar crowd: filtered noise swells, no voices — voices would need samples. */
  private crowd(t: number) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1100;
    bp.Q.value = 0.8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.16, t + 0.14);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.5);
    src.connect(bp).connect(g).connect(this.master!);
    src.start(t);
    src.stop(t + 1.6);
  }

  private tone(t: number, freq: number, dur: number, type: OscillatorType, gain: number) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private click(t: number, freq: number, dur: number, gain: number) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = freq;
    bp.Q.value = 6;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(bp).connect(g).connect(this.master!);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  private thump(t: number, freq: number, dur: number, gain: number) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.4, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.master!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private burst(t: number, dur: number, freq: number, gain: number) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(lp).connect(g).connect(this.master!);
    src.start(t);
    src.stop(t + dur + 0.02);
  }

  private arpeggio(t: number, freqs: number[], step: number, gain: number) {
    freqs.forEach((f, i) => this.tone(t + i * step, f, 0.22, 'triangle', gain));
  }

  // ── the jukebox ──────────────────────────────────────────────────────────

  /**
   * A honky-tonk loop for the bar: a walking bass under an off-beat piano
   * chord, both plucked triangle/square voices with fast decays. Sixteen steps
   * at 132 bpm. It is not going to win a Grammy; it is going to make the room
   * feel like a room, which is the job.
   */
  startMusic() {
    if (!this._musicOn || this._muted || !this.ctx || this.musicTimer !== null) return;
    const bpm = 132;
    const stepMs = (60_000 / bpm) / 2;
    this.musicGain!.gain.setTargetAtTime(0.5, this.ctx.currentTime, 0.4);
    this.musicStep = 0;
    this.musicTimer = window.setInterval(() => this.musicTick(), stepMs);
    this.musicTick();
  }

  stopMusic() {
    if (this.musicTimer !== null) {
      clearInterval(this.musicTimer);
      this.musicTimer = null;
    }
    if (this.musicGain && this.ctx) this.musicGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.25);
  }

  private musicTick() {
    const ctx = this.ctx;
    if (!ctx || !this.musicGain) return;
    const t = ctx.currentTime;
    const s = this.musicStep % 16;
    this.musicStep++;

    // I–IV–V in G, the most saloon progression there is.
    const roots = [98.0, 98.0, 130.81, 130.81, 98.0, 98.0, 146.83, 130.81];
    const root = roots[Math.floor(s / 2) % roots.length]!;

    // Walking bass on every step.
    const bassNote = s % 4 === 2 ? root * 1.5 : s % 4 === 3 ? root * 1.26 : root;
    this.pluck(t, bassNote, 'triangle', 0.16, 0.2);

    // Piano stabs on the off-beats.
    if (s % 2 === 1) {
      for (const mult of [2, 2.52, 3]) this.pluck(t, root * mult, 'square', 0.05, 0.14);
    }
    // A brush on the backbeat.
    if (s % 8 === 4) this.hat(t);
  }

  private pluck(t: number, freq: number, type: OscillatorType, gain: number, dur: number) {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(gain, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.musicGain!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private hat(t: number) {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 6000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.05, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    src.connect(hp).connect(g).connect(this.musicGain!);
    src.start(t);
    src.stop(t + 0.08);
  }
}

/** White noise, generated once and reused by every layer that needs it. */
function makeNoise(ctx: AudioContext, seconds: number): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

export const audio = new AudioKit();
